import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../App';

/**
 * VendorCatalogContext — Iter 220.99.99zz9ZZ15h (Feb 2026)
 *
 * Centralised cache of the org's vendor catalog so the BEO picker,
 * the BEO inline label typeahead, and the Market P&L pickers don't
 * each re-fetch the same list every time the operator opens a
 * different Add row.
 *
 * Mounting:
 *   <VendorCatalogProvider authed={!!user}>
 *
 * The fetch is gated on `authed=true` so the public landing/login
 * page doesn't fire a 401 GET. The list is fetched once and
 * exposed through `useVendorCatalog()`. Components can also call
 * `refresh()` after they POST/PUT a row.
 *
 * Filtering helpers (`byVendor`, `byCategory`) are memoised so each
 * Add row only re-renders if the relevant subset changes.
 */
const VendorCatalogContext = createContext({
  items: [],
  loading: false,
  refresh: () => {},
  byVendor: () => [],
  byCategory: () => [],
});

export const VendorCatalogProvider = ({ authed, children }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const r = await api.get('/vendor-catalog/items');
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      // Non-fatal: keep the prior list so pickers don't blank out
      // on a transient 5xx; the next refresh() will recover.
    } finally {
      setLoading(false);
    }
  }, [authed]);

  // Initial load + on auth flip.
  useEffect(() => {
    if (authed) refresh();
    else setItems([]);
  }, [authed, refresh]);

  // Listen for org-data changes so a CSV import / rolodex edit
  // surfaces in open pickers without a full page reload. The
  // axios interceptor broadcasts `kewl:data-changed` with the
  // mutation's URL — filter to catalog-impacting endpoints so we
  // don't thrash on unrelated saves.
  useEffect(() => {
    if (!authed) return;
    let timer = null;
    const onChange = (e) => {
      const url = (e.detail && e.detail.url) || '';
      const impacts = (
        url.includes('/vendor-catalog/')
        || url.includes('/csv/import')
        || (url.includes('/contacts') && (e.detail?.method || '') !== 'GET')
        || url.includes('/portal/vendor/items')
      );
      if (!impacts) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { refresh(); }, 350);
    };
    window.addEventListener('kewl:data-changed', onChange);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('kewl:data-changed', onChange);
    };
  }, [authed, refresh]);

  // Group once so repeated `byVendor()` calls are O(1).
  const byVendorIndex = useMemo(() => {
    const idx = new Map();
    for (const it of items) {
      const vid = it.vendor_id || '__unassigned__';
      if (!idx.has(vid)) idx.set(vid, []);
      idx.get(vid).push(it);
    }
    return idx;
  }, [items]);

  const byVendor = useCallback((vendorId) => {
    if (!vendorId) return items;
    return byVendorIndex.get(vendorId) || [];
  }, [items, byVendorIndex]);

  const byCategory = useCallback((category) => {
    if (!category) return items;
    const want = String(category).toLowerCase();
    return items.filter(it => String(it.category || '').toLowerCase() === want);
  }, [items]);

  const value = useMemo(() => ({ items, loading, refresh, byVendor, byCategory }), [items, loading, refresh, byVendor, byCategory]);

  return (
    <VendorCatalogContext.Provider value={value}>
      {children}
    </VendorCatalogContext.Provider>
  );
};

export const useVendorCatalog = () => useContext(VendorCatalogContext);

/** Convenience hook: returns just the slice relevant to a given
 *  (vendorId, category) pair so the picker components can replace
 *  their old fetch effect with a single line. */
export const useCatalogSlice = ({ vendorId, category } = {}) => {
  const { items, loading, refresh, byVendor, byCategory } = useVendorCatalog();
  const slice = useMemo(() => {
    if (vendorId && category) {
      return byVendor(vendorId).filter(it => String(it.category || '').toLowerCase() === String(category).toLowerCase());
    }
    if (vendorId) return byVendor(vendorId);
    if (category) return byCategory(category);
    return items;
  }, [items, vendorId, category, byVendor, byCategory]);
  return { items: slice, loading, refresh };
};

export default VendorCatalogContext;
